// @ts-nocheck
import { calendar_v3 } from "googleapis";
import { format, parseISO } from "date-fns";
import { toZonedTime, fromZonedTime } from "date-fns-tz";
import {
  CalendarScheduleRequest,
  CalendarScheduleSyncRequest,
  ICalendar,
  ICalendarEvent,
  ICalendarTransformer
} from "../entities/calendar";
import { AttendeeRequest, ScheduleRequest } from "../entities/schedule_request";
import {
  PresenceStatus,
  PRESENCE_STATUS_ACCEPTED,
  PRESENCE_STATUS_PENDING,
  PRESENCE_STATUS_REJECTED
} from "../entities/presence";
import {
  ScheduleVisibility,
  VISIBILITY_CONFIDENTIAL,
  VISIBILITY_PUBLIC,
  VISIBILITY_PRIVATE
} from "../entities/visibility";

type RecurringRequest = ScheduleRequest["recurring"];
type CalendarEvent = calendar_v3.Schema$Event;
type CalendarListEntry = calendar_v3.Schema$CalendarListEntry;

export class GoogleCalendarTransformer
  implements ICalendarTransformer<CalendarEvent>
{
  private static NON_HUMAN_ATTENDEE_RESOURCE_EMAIL_SUFFIX =
    "@resource.calendar.google.com";
  private static NON_HUMAN_ATTENDEE_GROUP_EMAIL_SUFFIX =
    "@group.calendar.google.com";
  private static PRESENCE_STATUS_MAP: { [key: string]: PresenceStatus } = {
    needsAction: PRESENCE_STATUS_PENDING,
    declined: PRESENCE_STATUS_REJECTED,
    tentative: PRESENCE_STATUS_ACCEPTED,
    accepted: PRESENCE_STATUS_ACCEPTED
  };
  private static PRESENCE_STATUS_MAP_REVERSED: { [key: string]: string } = {
    pending: "needsAction",
    rejected: "declined",
    accepted: "accepted"
  };
  private static VISIBILITY_MAP: { [key: string]: ScheduleVisibility } = {
    confidential: VISIBILITY_CONFIDENTIAL,
    default: VISIBILITY_PUBLIC,
    public: VISIBILITY_PUBLIC,
    private: VISIBILITY_PRIVATE
  };
  private static VISIBILITY_MAP_REVERSED: { [key: string]: string } = {
    confidential: "confidential",
    public: "public",
    private: "private"
  };

  private settings = {
    removeNonHumanAttendees: true
  };

  public transform(
    calendarId: string,
    event: CalendarEvent,
    recurringEvent: CalendarEvent | null = null
  ): CalendarScheduleSyncRequest {
    const scheduleRequest = this.createScheduleRequest(calendarId, event);
    const recurringScheduleRequest = recurringEvent
      ? this.createScheduleRequest(calendarId, recurringEvent)
      : null;

    // In some situations the recurring event might be a pure resource attendee
    // based event, yet actual attendees are part of the recurring instance

    if (
      recurringScheduleRequest !== null &&
      recurringScheduleRequest.attendees.length === 0
    ) {
      recurringScheduleRequest.attendees = [
        {
          email: scheduleRequest.organizer.email,
          name: scheduleRequest.organizer.name,
          presence_status: PRESENCE_STATUS_ACCEPTED,
          presence_type: null,
          user_id: null
        }
      ];
    }

    const request: CalendarScheduleSyncRequest = {
      schedule: scheduleRequest,
      recurring_schedule: recurringScheduleRequest
    };
    return request;
  }

  public transformBack(
    scheduleId: string,
    eventSource: ICalendarEvent,
    calendar: ICalendar
  ): CalendarEvent {
    const timeZone = eventSource.time_zone_id;

    // Our start and end times have a Z at the end, which we don't want
    const timeArgs = {
      start: {
        dateTime: eventSource.start_time
          ? GoogleCalendarTransformer.getTimeWithoutZ(eventSource.start_time)
          : null,
        timeZone: timeZone
      },
      end: {
        dateTime: eventSource.end_time
          ? GoogleCalendarTransformer.getTimeWithoutZ(eventSource.end_time)
          : null,
        timeZone: timeZone
      }
    };

    const participants = [];

    if (eventSource.attendees) {
      for (const attendee of eventSource.attendees) {
        participants.push({
          email: attendee.email,
          displayName: attendee.name,
          resource: false,
          responseStatus:
            GoogleCalendarTransformer.PRESENCE_STATUS_MAP_REVERSED[
              attendee.presence_status
            ]
        });
      }
    }

    if (eventSource.resource_attendees) {
      for (const attendee of eventSource.resource_attendees) {
        participants.push({
          email: attendee.email,
          displayName: attendee.name,
          resource: true,
          responseStatus:
            GoogleCalendarTransformer.PRESENCE_STATUS_MAP_REVERSED[
              attendee.presence_status
            ]
        });
      }
    }

    const metadata = GoogleCalendarTransformer.generateMetadata(
      scheduleId,
      eventSource.calendar_event_integration_id ?? null,
      calendar
    );

    const event: CalendarEvent = {
      summary: eventSource.title,
      location: eventSource.location,
      description: eventSource.description ?? "",
      start: timeArgs.start,
      end: timeArgs.end,
      attendees: participants,
      reminders: {
        useDefault: true
        /*
            useDefault: false,
            overrides: [
              { method: 'email', 'minutes': 24 * 60 },
              { method: 'popup', 'minutes': 10 },
            ],
            */
      },
      transparency: "opaque",
      visibility:
        GoogleCalendarTransformer.VISIBILITY_MAP_REVERSED[
          eventSource.visibility
        ],
      extendedProperties: { private: metadata }
    };

    if (
      eventSource.recurring_rules !== null &&
      eventSource.recurring_rules.length > 0
    ) {
      event.recurrence = GoogleCalendarTransformer.convertRRules(
        eventSource.recurring_rules,
        eventSource.recurring_end_time,
        timeZone
      );
    }

    return event;
  }

  private createScheduleRequest(
    calendarId: string,
    event: CalendarEvent
  ): CalendarScheduleRequest {
    if (
      !event ||
      !event.id ||
      !event.start ||
      !event.end ||
      !event.creator ||
      !event.creator.email
    ) {
      throw new Error("Missing required event parameters");
    }

    const startTime = event.start.dateTime ?? `${event.start.date}T00:00:00`,
      endTime = event.end.dateTime ?? `${event.end.date}T00:00:00`,
      timeZone = event.start.timeZone ?? "Etc/UTC";
    const creatorEmail = event.creator.email,
      creatorName = event.creator.displayName ?? null,
      creatorIsNonHuman =
        creatorEmail.endsWith(
          GoogleCalendarTransformer.NON_HUMAN_ATTENDEE_RESOURCE_EMAIL_SUFFIX
        ) ||
        creatorEmail.endsWith(
          GoogleCalendarTransformer.NON_HUMAN_ATTENDEE_GROUP_EMAIL_SUFFIX
        );
    const organizerEmail = event.organizer
        ? event.organizer.email
        : creatorEmail,
      organizerName = event.organizer
        ? event.organizer.displayName
        : creatorName,
      organizerIsNonHuman =
        organizerEmail.endsWith(
          GoogleCalendarTransformer.NON_HUMAN_ATTENDEE_RESOURCE_EMAIL_SUFFIX
        ) ||
        organizerEmail.endsWith(
          GoogleCalendarTransformer.NON_HUMAN_ATTENDEE_GROUP_EMAIL_SUFFIX
        );
    const humanCreatorReplacesNonHumanOrganizer =
      this.settings.removeNonHumanAttendees &&
      organizerIsNonHuman &&
      !creatorIsNonHuman &&
      creatorEmail != organizerEmail;

    let attendees: AttendeeRequest[] = [];
    let attendeesByEmail: { [key: string]: AttendeeRequest } = {};
    let resourceAttendees: AttendeeRequest[] = [];
    for (const attendee of event.attendees ?? []) {
      if (!attendee.email) {
        continue;
      }

      const attendeeEmail = attendee.email;
      const attendeeIsNonHumanResource =
        attendee.resource ||
        attendeeEmail.endsWith(
          GoogleCalendarTransformer.NON_HUMAN_ATTENDEE_RESOURCE_EMAIL_SUFFIX
        );

      if (attendeeIsNonHumanResource) {
        resourceAttendees.push({
          email: attendeeEmail,
          name: attendee.displayName ? attendee.displayName : null,
          presence_status: attendee.responseStatus
            ? GoogleCalendarTransformer.PRESENCE_STATUS_MAP[
                attendee.responseStatus
              ]
            : PRESENCE_STATUS_PENDING,
          presence_type: null,
          user_id: null
        });
      }

      if (
        this.settings.removeNonHumanAttendees &&
        (attendeeIsNonHumanResource ||
          attendeeEmail.endsWith(
            GoogleCalendarTransformer.NON_HUMAN_ATTENDEE_GROUP_EMAIL_SUFFIX
          ))
      ) {
        continue;
      }

      const attendeeRequest: AttendeeRequest = {
        email: attendeeEmail,
        name: attendee.displayName ? attendee.displayName : null,
        presence_status: attendee.responseStatus
          ? GoogleCalendarTransformer.PRESENCE_STATUS_MAP[
              attendee.responseStatus
            ]
          : PRESENCE_STATUS_PENDING,
        presence_type: null,
        user_id: null
      };

      attendees.push(attendeeRequest);
      attendeesByEmail[attendeeRequest.email] = attendeeRequest;
    }

    // If no attendee, assume organizer as attendee
    if (attendees.length === 0) {
      if (humanCreatorReplacesNonHumanOrganizer) {
        attendees.push({
          email: creatorEmail,
          name: creatorName ?? null,
          presence_status: PRESENCE_STATUS_ACCEPTED,
          presence_type: null,
          user_id: null
        });
      } else if (
        !organizerEmail.endsWith(
          GoogleCalendarTransformer.NON_HUMAN_ATTENDEE_GROUP_EMAIL_SUFFIX
        ) &&
        !organizerEmail.endsWith(
          GoogleCalendarTransformer.NON_HUMAN_ATTENDEE_RESOURCE_EMAIL_SUFFIX
        )
      ) {
        attendees.push({
          email: organizerEmail,
          name: organizerName ?? null,
          presence_status: PRESENCE_STATUS_ACCEPTED,
          presence_type: null,
          user_id: null
        });
      }
    }

    const scheduleId = GoogleCalendarTransformer.tryGetScheduleId(
      calendarId,
      event
    );

    // Look for metadata in any of the different formats already loaded

    const scheduleRequest: CalendarScheduleRequest = {
      creator: attendeesByEmail[creatorEmail] ?? {
        email: creatorEmail,
        name: creatorName ?? null,
        presence_status: null,
        presence_type: null,
        user_id: null
      },
      organizer: attendeesByEmail[
        humanCreatorReplacesNonHumanOrganizer ? creatorEmail : organizerEmail
      ] ?? {
        email: humanCreatorReplacesNonHumanOrganizer
          ? creatorEmail
          : organizerEmail,
        name:
          (humanCreatorReplacesNonHumanOrganizer
            ? creatorName
            : organizerName) ?? null,
        presence_status: null,
        presence_type: null,
        user_id: null
      },
      attendees: attendees,
      start: GoogleCalendarTransformer.convertGoogleTimeToTacticTime(
        startTime,
        timeZone
      ),
      original_start:
        event.originalStartTime && event.originalStartTime.dateTime
          ? GoogleCalendarTransformer.convertGoogleTimeToTacticTime(
              event.originalStartTime.dateTime,
              timeZone
            )
          : null,
      end: GoogleCalendarTransformer.convertGoogleTimeToTacticTime(
        endTime,
        timeZone
      ),
      time_zone: timeZone,
      recurring: event.recurrence
        ? this.convertRRulesToRecurringTask(event)
        : null,
      title: event.summary ?? null,
      description: event.description ?? null,
      visibility: event.visibility
        ? GoogleCalendarTransformer.VISIBILITY_MAP[event.visibility]
        : VISIBILITY_PUBLIC,
      integration: {
        schedule_id: !!scheduleId ? scheduleId : null,
        event_id: event.id,
        event_updated: event.updated,
        ical_id: event.iCalUID,
        url: event.htmlLink ?? null,
        event: event,
        is_deleted: event.status === "cancelled",
        resource_attendees:
          resourceAttendees.length > 0 ? resourceAttendees : null
      }
    };
    return scheduleRequest;
  }

  public static generateMetadata(
    scheduleId: string,
    eventId: string | null,
    calendar: ICalendar
  ): Record<string, string> {
    const metadata: Record<string, string> = { tacticEventId: eventId },
      metadataCalendarId = calendar.id.replace(/\-/g, "_"),
      scheduleKey = `tactic_schedule_${metadataCalendarId}`;

    metadata["tacticEventId"] = eventId ?? undefined;
    metadata["tacticScheduleId"] = scheduleId;
    metadata[scheduleKey] = scheduleId;

    return metadata;
  }

  public static tryGetScheduleId(
    calendarId: string,
    event: CalendarEvent
  ): string | null {
    const metadata: Record<string, string> | null =
      !!event.extendedProperties && !!event.extendedProperties.private
        ? event.extendedProperties.private
        : null;
    if (metadata === null) {
      return null;
    }

    const metadataCalendarId = calendarId.replace(/\-/g, "_"),
      scheduleKey = `tactic_schedule_${metadataCalendarId}`;

    return scheduleKey in metadata
      ? metadata[scheduleKey]
      : "tacticScheduleId" in metadata
        ? metadata["tacticScheduleId"]
        : "scheduleId" in metadata
          ? metadata["scheduleId"]
          : null;
  }

  public static tryGetEventId(
    calendarId: string,
    event: CalendarEvent
  ): string | null {
    const eventId: string | null =
      !!event.extendedProperties && !!event.extendedProperties.private
        ? event.extendedProperties.private["tacticEventId"]
        : null;
    return eventId;
  }

  // Taken from integrations@google-client.ts
  public static convertGoogleTimeToTacticTime(
    time: string,
    timeZone?: string | null
  ): string {
    if (!time.includes("T")) {
      return `${time}T00:00:00Z`;
    }

    const date = parseISO(time);
    if (!timeZone) {
      return date.toISOString().split(".")[0] + "Z";
    }

    const utcDate = new Date(date.toUTCString());
    const zonedTime = toZonedTime(utcDate, timeZone);
    return format(zonedTime, "yyyy-MM-dd'T'HH:mm:ss'Z'");
  }

  // Inspired from integrations@google-client.ts
  private convertRRulesToRecurringTask(event: CalendarEvent): RecurringRequest {
    const parsedRules = event.recurrence.reduce(
      (acc: string[], rule: string) => {
        const splitRule = rule.split("RRULE:");
        if (splitRule.length > 1) {
          acc.push(splitRule[1]);
        }
        return acc;
      },
      []
    );
    const timeZone = event.start.timeZone ?? "Etc/UTC";
    let start =
      event.originalStartTime && event.originalStartTime.dateTime
        ? event.originalStartTime.dateTime
        : (event.start.dateTime ?? `${event.start.date}T00:00:00`);
    let end: string | null = null;

    if (parsedRules.length > 0) {
      const match = parsedRules[0].match(/UNTIL=([^;]+)/);
      const until = match ? match[1] : null;
      if (until) {
        const parsedTime = parseISO(until);
        const utcTime = toZonedTime(parsedTime, "UTC");

        const startUtc = parseISO(start);
        // In some situations, the start event date is after the recurring end
        if (startUtc >= parsedTime && event.created) {
          start = format(
            toZonedTime(parseISO(event.created), "UTC"),
            "yyyy-MM-dd'T'HH:mm:ss'Z'"
          );
        }

        end = format(utcTime, "yyyy-MM-dd'T'HH:mm:ss'Z'");
      }
    }

    return {
      rrules: parsedRules,
      start: GoogleCalendarTransformer.convertGoogleTimeToTacticTime(
        start,
        "UTC"
      ),
      end:
        end !== null
          ? GoogleCalendarTransformer.convertGoogleTimeToTacticTime(end, "UTC")
          : null
    };
  }

  private static getTimeWithoutZ(date: string): string {
    return date.charAt(date.length - 1) === "Z" ? date.slice(0, -1) : date;
  }

  private static convertRRules(
    rrules: string[],
    end: string | null,
    timeZoneId: string
  ): string[] {
    let rules = rrules.map((rule) => "RRULE:" + rule);
    if (end !== null) {
      if (end.endsWith("Z")) {
        end = end.slice(0, -1);
        end = end.replace("T", " ");
      }
      const utcEnd = fromZonedTime(end, timeZoneId);
      rules = rules.map(
        (rule) => `${rule};UNTIL=${format(utcEnd, "yyyyMMdd'T'HHmmss'Z'")}`
      );
    }
    return rules;
  }
}
